<template>
    <common-page-loader :isLoading="isLoading">
        <div :class="appLayoutClass">
            <nav class="subdomain-layout__topnav">
                <div :class="overlayClass"></div>
                <header
                    v-if="isTablet"
                    v-click-away="closeSidenav"
                    :class="sidenavClass"
                    @mouseenter="sideNavMouseOver()"
                    @mouseleave="sideNavMouseLeave()"
                >
                    <app-sidenav :isSmall="!isLargeSideNav" @close-sidenav="closeSidenav"></app-sidenav>
                </header>
                <subdomain-topnav @open-sidenav="openSidenav"></subdomain-topnav>
            </nav>

            <main :class="contentClass">
                <slot></slot>
                <div id="modal-target"></div>
            </main>
            <complete-profile-modal
                v-if="isCompleteProfileModalOpen"
                @close="isCompleteProfileModalOpen = false"
            ></complete-profile-modal>
            <cookie-modal
                v-if="showCookiesModal && !userIsOnIphone"
                @cookiesAccepted="cookiesAccepted"
                @close="declineCookies"
            ></cookie-modal>
        </div>
    </common-page-loader>
</template>

<script>
    import Store from '@/store'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'
    import SubdomainTopnav from 'components/layouts/components/subdomain-layout/SubdomainTopnav.vue'
    import { importComponents } from '~/helpers'
    import VersionUpdateServiceWorkerMixin from 'mixins/VersionUpdateServiceWorkerMixin'
    import ThemeEnum from 'enums/theme.enum'
    import CompleteProfileModal from 'components/modals/CompleteProfileModal'
    import CookieModal from 'components/modals/CookieModal'
    import { loadFacebookPixel } from '../../meta-pixel'
    import { googleOneTap } from 'vue3-google-login'
    import AuthService from 'services/AuthService'
    import AuthAPI from 'api/AuthAPI'
    import LoginType from 'enums/login_type.enum'

    export default {
        mixins: [WindowResizeMixin, VersionUpdateServiceWorkerMixin],

        components: {
            SubdomainTopnav,
            ...importComponents(require.context('components/layouts/components/main-layout')),
            CompleteProfileModal,
            CookieModal,
        },

        props: {
            contentPadding: { type: String, default: 'small' },
        },

        data() {
            return {
                isSidenavOpen: false,
                isSideNavHovered: false,
                isCompleteProfileModalOpen: false,
                showCookiesModal: !localStorage.getItem('cookieConsent'),
                userIsOnIphone: localStorage.getItem('deviceIphone'),
            }
        },

        computed: {
            isLargeSideNav() {
                return this.isSideNavHovered && !this.isTablet
            },

            appLayoutClass() {
                return {
                    'subdomain-layout': true,
                    [`subdomain-layout--${this.theme}`]: true,
                    'subdomain-layout__hover': this.isLargeSideNav,
                }
            },

            theme() {
                if (Store.getters['data/getSubdomainCompany']?.theme) {
                    if ([ThemeEnum.BLACK, ThemeEnum.NAVY_BLACK].includes(Store.getters['data/getSubdomainCompany']?.theme)) {
                        document.body.style.backgroundColor = '$subdomain-midnight'

                        document.body.classList.add('subdomain-black')
                    }
                    return Store.getters['data/getSubdomainCompany']?.theme.toLowerCase()
                }
                return ''
            },

            sidenavClass() {
                return {
                    'subdomain-layout__sidenav': true,
                    'subdomain-layout__sidenav--closed': !this.isSidenavOpen,
                    'subdomain-layout__sidenav--large': this.isLargeSideNav,
                }
            },

            overlayClass() {
                return {
                    'subdomain-layout__overlay': this.isSidenavOpen,
                }
            },

            contentClass() {
                return {
                    'subdomain-layout__content': true,
                    [`subdomain-layout__content--padding-size-${this.contentPadding}`]: true,
                }
            },

            isLoading() {
                return Store.getters['configuration/getIsLoading']
            },

            subdomainCompany() {
                return Store.getters['data/getSubdomainCompany']
            },
        },

        methods: {
            cookiesAccepted() {
                localStorage.setItem('cookieConsent', true)
                this.showCookiesModal = false
                loadFacebookPixel()
            },
            declineCookies() {
                localStorage.setItem('cookieConsent', false)
                this.showCookiesModal = false
            },
            closeSidenav() {
                this.isSidenavOpen = false
            },

            openSidenav() {
                this.isSidenavOpen = true
            },

            sideNavMouseLeave() {
                if (this.isLoading) {
                    return
                }
                this.isSideNavHovered = false
            },

            sideNavMouseOver() {
                this.isSideNavHovered = true
            },

            showCompleteProfileModal() {
                if (
                    this.$auth.getUser().id &&
                    this.$auth.isLoggedIn() &&
                    !this.$auth.isUserCompleteProfile() &&
                    !localStorage.getItem('completeProfileModalShown')
                ) {
                    localStorage.setItem('completeProfileModalShown', true)
                    this.isCompleteProfileModalOpen = true
                }
            },

            showGSignupButton() {
                return !navigator.userAgent.includes('Instagram') && !navigator.userAgent.match(/FBAN|FBAV/i)
            },

            createSession(data) {
                this.$auth.createSession({
                    accessToken: data.access_token,
                    refreshToken: data.refresh_token,
                    loginType: LoginType.GOOGLE,
                })
                window.location.reload()
            },

            checkGoogleOneTap() {
                if (
                    !AuthService.isLoggedIn() &&
                    this.$auth.isSubdomainActive() &&
                    Store.getters['data/getSubdomainCompany'].googleLoginEnabled &&
                    this.showGSignupButton() &&
                    !this.$route.query['tkn'] &&
                    !this.userIsOnIphone
                ) {
                    googleOneTap()
                        .then((response) => {
                            AuthAPI.loginGoogleOneTap({
                                accessToken: response.credential,
                                language: this.$i18n.locale,
                            })
                                .then((res) => {
                                    this.createSession(res.payload)
                                })
                                .catch(() => {})
                        })
                        .catch(() => {
                            this.$toast.error(this.$t('common.errors.common_error'))
                        })
                }
            },

            fixUrl() {
                const url = window.location.href

                if (url.includes('fbclid')) {
                    const urlObj = new URL(url)
                    const finalUrl = url.replace(urlObj.search, '')
                    window.location.replace(finalUrl)
                }
            },
        },

        watch: {
            $route() {
                this.showCompleteProfileModal()
                this.closeSidenav()
            },

            subdomainCompany: {
                handler(company) {
                    if (company?.googleLoginEnabled) {
                        this.checkGoogleOneTap()
                    }
                },
            },
        },

        mounted() {
            this.fixUrl()
            this.showCompleteProfileModal()
            if (localStorage.getItem('cookieConsent') === 'true') {
                loadFacebookPixel()
            }
        },
    }
</script>
